import { isValid, parse } from 'date-fns';

const DEFAULT_BOOLEAN_STRING_VALUE = {
  true: ['true', 'yes'],
  false: ['false', 'no'],
};

const DEFAULT_DATE_TEMPLATES = [
  'dd.MM.yyyy',
  'dd.MM.yy',
  'dd.MM.yy HH:mm',
  'dd.MM.yyyy HH:mm',
  'dd.MM.yyyy HH:mm:ss',
  'dd.MM.yy HH:mm:ss',
  'dd.MM.yyyy, HH:mm:ss',
  'dd.MM.yy, HH:mm:ss',
  'dd.MM.yyyy, HH:mm',
  'dd.MM.yy, HH:mm',
];

const regExpDict: Record<string, string> = {};

type TValuesArray = (string | number | boolean | Date | null)[];

const parseTabularTextRows = (text: string): string[][] => {
  const normalizedText = text.replaceAll('\r', '');
  const rows: string[][] = [];
  const currentRow: string[] = [];
  let currentValue = '';
  let isQuoted = false;

  for (let i = 0; i < normalizedText.length; i += 1) {
    const char = normalizedText[i];

    if (char === '"') {
      const nextChar = normalizedText[i + 1];

      if (isQuoted && nextChar === '"') {
        currentValue += '"';
        i += 1;
        continue;
      }

      isQuoted = !isQuoted;
      continue;
    }

    if (char === '\t' && !isQuoted) {
      currentRow.push(currentValue);
      currentValue = '';
      continue;
    }

    if (char === '\n' && !isQuoted) {
      currentRow.push(currentValue);
      rows.push([...currentRow]);
      currentRow.length = 0;
      currentValue = '';
      continue;
    }

    currentValue += char;
  }

  if (currentValue.length > 0 || currentRow.length > 0) {
    currentRow.push(currentValue);
    rows.push(currentRow);
  }

  return rows;
};

const getDateRegExpsTemplatesByDateTemplate = (templates: string[]): string[] => {
  const regExps = [];

  for (const template of templates) {
    if (regExpDict[template]) {
      regExps.push(regExpDict[template]);
      continue;
    }

    const regExpTemp = template.replaceAll(
      /^d{1,2}|M{1,2}|y{1,4}|(H|h){1,2}|m{1,2}|s{1,2}$/g,
      (sub): string => `[0-9]{${sub.length}}`,
    );

    const regExp = `^${regExpTemp}$`;
    regExps.push(regExp);

    regExpDict[template] = regExp;
    regExpDict[regExp] = template;
  }

  return regExps;
};

export const convertExcelStringToValueArrays = (
  text: string,
  options?: {
    boolean?: {
      true?: string[];
      false?: string[];
    };
    date?: string[];
  },
): TValuesArray[] => {
  const rawRows = parseTabularTextRows(text).filter((row) => row.some((value) => value !== ''));

  const booleanTrue = [...DEFAULT_BOOLEAN_STRING_VALUE.true, ...(options?.boolean?.true ?? [])];
  const booleanFalse = [...DEFAULT_BOOLEAN_STRING_VALUE.false, ...(options?.boolean?.false ?? [])];
  const date = [...DEFAULT_DATE_TEMPLATES, ...(options?.date ?? [])];

  const dateRegExpsTemplates = getDateRegExpsTemplatesByDateTemplate(date);

  const rows: TValuesArray[] = [];

  for (const rowValues of rawRows) {
    const values: TValuesArray = [];

    valueFor: for (const value of rowValues) {
      if (value !== '' && value !== '-') {
        const maybeNumber = value.replace(/\s/g, '');
        if (!Number.isNaN(Number(maybeNumber))) {
          values.push(Number(maybeNumber));
          continue;
        }

        if (maybeNumber.match(/^-?[0-9]+,?[0-9]*$/)) {
          const parsedValue = Number(maybeNumber.replace(',', '.'));

          if (!Number.isNaN(parsedValue)) {
            values.push(parsedValue);
            continue;
          }
        }

        if (booleanTrue.includes(value.toLowerCase())) {
          values.push(true);
          continue;
        }

        if (booleanFalse.includes(value.toLowerCase())) {
          values.push(false);
          continue;
        }

        for (const dateRegExpsTemplate of dateRegExpsTemplates) {
          const match = value.match(new RegExp(dateRegExpsTemplate));

          if (match && regExpDict[dateRegExpsTemplate]) {
            const parsedDate = parse(value, regExpDict[dateRegExpsTemplate], new Date());

            if (isValid(parsedDate)) {
              values.push(parsedDate);
              continue valueFor;
            }
          }
        }

        values.push(value);
      } else {
        values.push(null);
      }
    }

    rows.push(values);
  }

  return rows;
};
